import { RoutePath } from "@app/constants";
import router from "@app/router";
import UserBookDurationService from "@app/services/http/user-book-duration.service";
import { clearAudiobook, selectAudiobook } from "@app/store/audiobook";
import { DurationMap } from "@app/types";
import { fancyTimeFormat } from "@app/utils/util";
import { Images } from "@assets/images";
import useEventListener from "@core/hooks/use-event-listener";
import { isNumber } from "@core/utils/util";
import clsx from "clsx";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const audioPlaybackRates = [
  { value: 0.25, label: "0,25x" },
  { value: 0.5, label: "0,5x" },
  { value: 0.75, label: "0,75x" },
  { value: 1, label: "Bình thường" },
  { value: 1.25, label: "1,25x" },
  { value: 1.5, label: "1,5x" },
  { value: 1.75, label: "1,75x" },
  { value: 2, label: "2x" },
];

function Audiobook() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const durationRef = useRef<Map<number, DurationMap>>(new Map());
  const playbackRateRef = useRef<HTMLDivElement>(null);

  const [isPlaylistShow, setIsPlaylistShow] = useState(false);
  const [isMute, setIsMute] = useState(false);
  const [volumn, setVolumn] = useState(100);
  const [oldVolumn, setOldVolumn] = useState(100);
  const [linkAudioIdSelected, setLinkAudioIdSelected] = useState<number>();
  const [currentLinkAudio, setCurrentLinkAudio] = useState<string>();
  const [duration, setDuration] = useState<number>();
  const [currentTime, setCurrentTime] = useState<number>();
  const [percentTime, setPercentTime] = useState(0);
  const [isPause, setIsPause] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaybackRateShow, setIsPlaybackRateShow] = useState(false);

  const audiobookState = useSelector(selectAudiobook);

  const dispatch = useDispatch();

  const storeDuration = () => {
    for (const [bookId, durationMap] of Array.from(durationRef.current)) {
      let sum = 0;

      if (durationMap) {
        if (
          isNumber(durationMap.duration) &&
          Number(durationMap.duration) >= 0
        ) {
          sum += Number(durationMap.duration);
        }

        if (
          isNumber(durationMap.startTime) &&
          isNumber(durationMap.endTime) &&
          Number(durationMap.endTime) >= Number(durationMap.startTime)
        ) {
          sum += Number(durationMap.endTime) - Number(durationMap.startTime);
        }
      }

      sum = Math.round(sum);

      if (sum <= 0) {
        return;
      }

      setTimeout(() => {
        try {
          UserBookDurationService.storeDuration({ bookId, duration: sum });
        } catch (error) {
          /* empty */
        } finally {
          durationRef.current.delete(bookId);
        }
      }, 0);
    }
  };

  useEventListener("beforeunload", () => {
    storeDuration();
  });

  useEffect(() => {
    if (audiobookState.audiobookData.audioList.length) {
      setLinkAudioIdSelected(audiobookState.audiobookData.audioList[0].id);
      setCurrentLinkAudio(audiobookState.audiobookData.audioList[0].link);

      if (audioRef.current) {
        audioRef.current.load();
        audioRef.current.playbackRate = playbackRate;
        audioRef.current.play();
        setIsPause(false);
      }
    }

    if (
      !audiobookState.isPanelShow &&
      audioRef.current &&
      !audioRef.current.paused
    ) {
      audioRef.current.pause();
    }

    if (audiobookState.audiobookData.bookId) {
      storeDuration();
    }
  }, [audiobookState.audiobookData]);

  const handlePanelCloseClick = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setIsPlaylistShow(false);
    setIsPlaybackRateShow(false);
    setCurrentLinkAudio(undefined);
    dispatch(clearAudiobook());

    storeDuration();
  };

  const handleVolumnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (Number(event.target.value) === 0) {
      setIsMute(true);
    } else {
      setIsMute(false);
    }

    setVolumn(Number(event.target.value));
    setOldVolumn(Number(event.target.value));

    if (audioRef.current) {
      audioRef.current.volume = Number(event.target.value) / 100;
    }
  };

  const handleVolumnClick = () => {
    if (!isMute) {
      setVolumn(0);

      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    } else {
      setVolumn(oldVolumn);

      if (audioRef.current) {
        audioRef.current.volume = oldVolumn / 100;
      }
    }

    setIsMute(!isMute);
  };

  const handlePlaylistClick = () => {
    setIsPlaylistShow(!isPlaylistShow);
  };

  const handlePlaybackRateClick = () => {
    setIsPlaybackRateShow((prev) => !prev);
  };

  const setAudio = (item: { id: number; name: string; link: string }) => {
    setLinkAudioIdSelected(item.id);
    setCurrentLinkAudio(item.link);

    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.play();
    }
  };

  useEffect(() => {
    if (!audioRef.current) return;

    audioRef.current.playbackRate = playbackRate;
  }, [playbackRate, currentLinkAudio]);

  const handleAudioItemClick = (item: {
    id: number;
    name: string;
    link: string;
  }) => {
    setAudio(item);
    setIsPause(false);
  };

  const handleBookNameClick = () => {
    if (audiobookState.audiobookData.bookId) {
      router.navigate(
        `/${RoutePath.BOOK_DETAIL}/${audiobookState.audiobookData.bookId}`
      );
    }
  };

  const handleDurationChange = (e: ChangeEvent<HTMLAudioElement>) => {
    if (!isNaN(e.target.duration)) {
      setDuration(e.target.duration);
    }
  };

  const handlePlay = (e: ChangeEvent<HTMLAudioElement>) => {
    const bookId = audiobookState.audiobookData.bookId;

    if (durationRef.current.has(bookId)) {
      const currentPart = durationRef.current.get(bookId);

      if (
        currentPart &&
        isNumber(currentPart.startTime) &&
        isNumber(currentPart.endTime) &&
        Number(currentPart.endTime) > Number(currentPart.startTime)
      ) {
        durationRef.current.set(bookId, {
          ...currentPart,
          duration:
            Number(currentPart.endTime) -
            Number(currentPart.startTime) +
            Number(currentPart.duration ?? 0),
          startTime: e.target.currentTime,
          endTime: e.target.currentTime,
        });
      }
    } else {
      durationRef.current.set(bookId, {
        startTime: e.target.currentTime,
        endTime: e.target.currentTime,
      });
    }
  };

  const handleTimeUpdate = (e: ChangeEvent<HTMLAudioElement>) => {
    if (!isNaN(e.target.currentTime)) {
      setCurrentTime(e.target.currentTime);
    }

    if (!isNaN(e.target.currentTime) && duration) {
      const percentTime = (e.target.currentTime / duration) * 100;

      setPercentTime(percentTime);
    }

    const bookId = audiobookState.audiobookData.bookId;

    if (durationRef.current.has(bookId)) {
      const currentPart = durationRef.current.get(bookId);

      if (currentPart) {
        if (
          Math.round(e.target.currentTime) === 0 &&
          isNumber(currentPart.startTime) &&
          isNumber(currentPart.endTime) &&
          Number(currentPart.endTime) > Number(currentPart.startTime)
        ) {
          durationRef.current.set(bookId, {
            ...currentPart,
            duration:
              Number(currentPart.endTime) -
              Number(currentPart.startTime) +
              Number(currentPart.duration ?? 0),
            startTime: e.target.currentTime,
            endTime: e.target.currentTime,
          });
        } else {
          durationRef.current.set(bookId, {
            ...currentPart,
            endTime: e.target.currentTime,
          });
        }
      }
    }

    if (e.target.ended) {
      const currentAudioIndex =
        audiobookState.audiobookData.audioList.findIndex(
          (item) => item.id === linkAudioIdSelected
        );

      if (
        currentAudioIndex !== -1 &&
        currentAudioIndex < audiobookState.audiobookData.audioList.length - 1
      ) {
        setAudio(audiobookState.audiobookData.audioList[currentAudioIndex + 1]);
      } else {
        setIsPause(true);
      }
    }
  };

  const handlePercentTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (duration) {
      setPercentTime(Number(e.target.value));

      if (audioRef.current) {
        const currentStartTime = (Number(e.target.value) / 100) * duration;

        audioRef.current.currentTime = currentStartTime;

        const bookId = audiobookState.audiobookData.bookId;

        if (durationRef.current.has(bookId)) {
          const currentPart = durationRef.current.get(bookId);

          if (
            currentPart &&
            Math.round(currentStartTime) !==
              Math.round(Number(currentPart.endTime)) &&
            Number(currentPart.endTime) > Number(currentPart.startTime)
          ) {
            durationRef.current.set(bookId, {
              ...currentPart,
              duration:
                Number(currentPart.endTime) -
                Number(currentPart.startTime) +
                Number(currentPart.duration ?? 0),
              startTime: currentStartTime,
              endTime: currentStartTime,
            });
          }
        }
      }
    }
  };

  const handlePauseClick = () => {
    setIsPause(!isPause);

    if (audioRef.current) {
      if (!isPause) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handlePlaybackRateSelect = (nextRate: number) => {
    if (!Number.isFinite(nextRate) || nextRate <= 0) return;

    setPlaybackRate(nextRate);
    setIsPlaybackRateShow(false);
  };

  const playbackRateLabel =
    audioPlaybackRates.find((item) => item.value === playbackRate)?.label ??
    "Bình thường";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        playbackRateRef.current &&
        !playbackRateRef.current.contains(event.target as Node)
      ) {
        setIsPlaybackRateShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePreviousClick = () => {
    const currentAudioIndex = audiobookState.audiobookData.audioList.findIndex(
      (item) => item.id === linkAudioIdSelected
    );

    if (currentAudioIndex !== -1 && currentAudioIndex > 0) {
      setAudio(audiobookState.audiobookData.audioList[currentAudioIndex - 1]);
    }
  };

  const handleNextClick = () => {
    const currentAudioIndex = audiobookState.audiobookData.audioList.findIndex(
      (item) => item.id === linkAudioIdSelected
    );

    if (
      currentAudioIndex !== -1 &&
      currentAudioIndex < audiobookState.audiobookData.audioList.length - 1
    ) {
      setAudio(audiobookState.audiobookData.audioList[currentAudioIndex + 1]);
    }
  };

  const shouldClickPrevious = () => {
    const currentAudioIndex = audiobookState.audiobookData.audioList.findIndex(
      (item) => item.id === linkAudioIdSelected
    );

    return currentAudioIndex !== -1 && currentAudioIndex <= 0;
  };

  const shouldClickNext = () => {
    const currentAudioIndex = audiobookState.audiobookData.audioList.findIndex(
      (item) => item.id === linkAudioIdSelected
    );

    return (
      currentAudioIndex !== -1 &&
      currentAudioIndex >= audiobookState.audiobookData.audioList.length - 1
    );
  };

  const handleBackwardClick = () => {
    if (typeof currentTime === "number" && duration) {
      const nextTime = currentTime - 10 <= 0 ? 0 : currentTime - 10;

      const percentTime = (nextTime / duration) * 100;

      setPercentTime(percentTime);

      if (audioRef.current) {
        audioRef.current.currentTime = nextTime;
      }
    }
  };

  const handleForwardClick = () => {
    if (typeof currentTime === "number" && duration) {
      const nextTime =
        currentTime + 10 >= duration ? duration : currentTime + 10;

      const percentTime = (nextTime / duration) * 100;

      setPercentTime(percentTime);

      if (audioRef.current) {
        audioRef.current.currentTime = nextTime;
      }
    }
  };

  return (
    <div
      className={clsx(
        "fixed bottom-0 left-0 w-full h-[108px] select-none",
        "bg-[rgba(23,26,32,0.90)] backdrop-blur-[18px]",
        "lg:px-[100px] md:px-10 py-4 text-white z-10 hidden",
        {
          "!block": !!audiobookState.isPanelShow,
        }
      )}
    >
      <div className="h-full flex items-center justify-between gap-3">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={handleBookNameClick}
        >
          <img
            className="w-[46px] h-[69px] object-contain"
            src={audiobookState.audiobookData.thumbnail}
            alt=""
          />
          <div>
            <div className="font-[SVN-Gilroy] text-sm font-semibold tracking-[0.028px]">
              {audiobookState.audiobookData.bookName}
            </div>
            <div className="text-[#C0C5CF] font-[SVN-Gilroy] text-xs font-medium tracking-[0.028px]">
              {audiobookState.audiobookData.author}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-4 justify-center items-center">
            <img
              className="size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer"
              src={Images.BackwardIcon.default}
              alt=""
              onClick={handleBackwardClick}
            />
            <img
              className={clsx(
                "size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer",
                {
                  "!cursor-not-allowed": shouldClickPrevious(),
                }
              )}
              src={Images.PreviousIcon.default}
              alt=""
              onClick={handlePreviousClick}
            />
            <img
              className="cursor-pointer"
              src={
                !isPause ? Images.PauseIcon.default : Images.PlayIcon.default
              }
              alt=""
              onClick={handlePauseClick}
            />
            <img
              className={clsx(
                "size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer",
                {
                  "!cursor-not-allowed": shouldClickNext(),
                }
              )}
              src={Images.NextIcon.default}
              alt=""
              onClick={handleNextClick}
            />
            <img
              className="size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer"
              src={Images.ForwardIcon.default}
              alt=""
              onClick={handleForwardClick}
            />
          </div>
          <div className="flex gap-2 items-center justify-center">
            <div className="font-[SVN-Gilroy] text-[#DFE2E7] text-xs font-medium tracking-[0.024px]">
              {typeof currentTime === "number"
                ? fancyTimeFormat(currentTime)
                : "00:00"}
            </div>
            <div className="xl:w-[556px] lg:w-[350px] md:w-[180px] flex">
              <input
                type="range"
                min={0}
                max={100}
                value={percentTime}
                onChange={handlePercentTimeChange}
                className={clsx(
                  "w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[6px]",
                  "[&::-webkit-slider-thumb]:translate-y-[-31%] [&::-webkit-slider-runnable-track]:rounded-full",
                  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FFFFFF]",
                  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[16px]",
                  "[&::-webkit-slider-runnable-track]:bg-[#38499b]"
                )}
              />
            </div>
            <div className="font-[SVN-Gilroy] text-[#DFE2E7] text-xs font-medium tracking-[0.024px]">
              {duration ? fancyTimeFormat(duration) : "00:00"}
            </div>
          </div>
        </div>
        <div className="flex gap-5">
          <div className="relative" ref={playbackRateRef}>
            <div
              className={clsx(
                "h-8 rounded-md bg-[#272C36] border border-[#4E586C] px-2 text-xs text-white outline-none flex items-center gap-2 cursor-pointer",
                {
                  "border-[#5A82D7]": isPlaybackRateShow,
                }
              )}
              onClick={handlePlaybackRateClick}
            >
              <img
                className="size-4"
                src={Images.ListenAudioSpeedIcon.default}
                alt=""
              />
              <span className="font-[SVN-Gilroy]">{playbackRateLabel}</span>
            </div>
            <div
              className={clsx(
                "absolute right-0 bottom-10 w-[132px] rounded-lg bg-[#171A20] border border-solid border-[#272C36] p-2 hidden z-20",
                {
                  "!block": isPlaybackRateShow,
                }
              )}
            >
              <div className="flex flex-col">
                {audioPlaybackRates.map((item) => (
                  <div
                    key={item.value}
                    className={clsx(
                      "px-2 py-1 text-sm font-[SVN-Gilroy] cursor-pointer rounded-md hover:bg-[#272C36]",
                      {
                        "text-[#5A82D7]": item.value === playbackRate,
                        "text-white": item.value !== playbackRate,
                      }
                    )}
                    onClick={() => handlePlaybackRateSelect(item.value)}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                !isPlaylistShow
                  ? Images.ListViewCircle.default
                  : Images.ListViewShowing.default
              }
              alt=""
              onClick={handlePlaylistClick}
            />
            <div
              className={clsx(
                "absolute h-[382px] w-[336px] bg-white bottom-10 right-0 rounded-xl hidden",
                {
                  "!block": !!isPlaylistShow,
                }
              )}
            >
              <div className="relative text-[#171A20] pt-5 pl-6 pb-4 pr-4 font-[SVN-Gilroy] font-semibold text-lg">
                Danh mục phát ({audiobookState.audiobookData.audioList.length})
                <hr className="absolute w-[282px] bottom-1 border-t-[#DFE2E7]" />
              </div>
              <div className="h-[318px] overscroll-contain overflow-y-auto pl-6 pr-[30px] text-[#3A4251] flex flex-col gap-4">
                {!!audiobookState.audiobookData.audioList.length &&
                  audiobookState.audiobookData.audioList.map((item) => (
                    <div
                      key={item.id}
                      className={clsx(
                        "font-[SVN-Gilroy] font-medium tracking-[0.024px] py-1 hover:bg-gray-200 cursor-pointer rounded-md",
                        {
                          "text-[#38499b]": linkAudioIdSelected === item.id,
                        }
                      )}
                      onClick={() => handleAudioItemClick(item)}
                    >
                      {item.name}
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                !isMute
                  ? Images.VolumnIcon.default
                  : Images.VolumnCrossIcon.default
              }
              alt=""
              onClick={handleVolumnClick}
            />
            <input
              type="range"
              min={0}
              max={100}
              value={volumn}
              onChange={handleVolumnChange}
              className={clsx(
                "w-[84px] appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[6px]",
                "[&::-webkit-slider-thumb]:translate-y-[-31%] [&::-webkit-slider-runnable-track]:rounded-full",
                "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FFFFFF]",
                "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[16px]",
                "[&::-webkit-slider-runnable-track]:bg-[#38499b]"
              )}
            />
          </div>
        </div>
      </div>
      <div
        className="absolute top-3 right-3 cursor-pointer hover:bg-gray-500 hover:rounded-full"
        onClick={handlePanelCloseClick}
      >
        <img src={Images.CloseIcon.default} alt="" />
      </div>
      <audio
        ref={audioRef}
        hidden
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
      >
        <source src={currentLinkAudio} type="" />
      </audio>
    </div>
  );
}

export default Audiobook;
